import { useNavigate, useSearchParams, useLocation } from 'react-router-dom';
import { useState, useEffect } from 'react';
import { useRequest } from 'ahooks';

import { useLayout } from '~hooks';
import { Onboarding } from '~services';
import {
  Button,
  Footer,
  Modal,
  ButtonWithArrowRight,
  Toast,
  Loading,
} from '~components';
import { ONBOARDING_TOKEN } from '~constants/localStorageIndexes';

import * as Styled from './styled';
import { Form } from './sections';
import { Form as FormInterface, Forms, Values } from './types';
import { formatPayload } from './formatters';
import * as amplitude from '@amplitude/analytics-browser';

function Register() {
  const [open, setOpen] = useState(false);
  const [isToastVisible, setIsToastVisible] = useState(false);
  const [isToastVisibleForm, setIsToastVisibleForm] = useState(false);
  const [
    isToastVisibleForRepresentativeSavedInMemory,
    setIsToastVisibleForRepresentativeSavedInMemory,
  ] = useState(false);
  const [isToastVisibleForRemoveInMemory, setIsToastVisibleForRemoveInMemory] =
    useState(false);
  const [formRepresentativeSubmited, setFormRepresentativeSubmited] =
    useState(false);

  const [formValidityList, setFormValidityList] = useState<boolean[]>([]);

  const [forms, setForms] = useState<Forms>([
    { status: 'pendente', type: 'primary' },
  ]);

  const [isModalAlertDataDoubleOpen, setIsModalAlertDataDoubleOpen] =
    useState(false);

  const legalNature = localStorage.getItem('legal_nature');
  const individualCompaniesLegalNature = ['2135'];
  const isIndividualCompany =
    individualCompaniesLegalNature.includes(legalNature);
  const isPrimaryFormPendent = forms[0].status === 'pendente';

  const navigate = useNavigate();
  const location = useLocation();
  const { setLayout } = useLayout();
  const [searchParams] = useSearchParams();
  const onboardId = searchParams.get('id');

  const { runAsync: saveRepresentatives, loading } = useRequest(
    Onboarding.saveBusinessRepresentative,
    {
      manual: true,
    }
  );

  useEffect(() => {
    amplitude.track({
      event_type: 'documentation_sent_started',
    });
  }, []);

  useEffect(() => {
    setLayout({
      header: {
        title: isIndividualCompany
          ? 'Certificado MEI e dados do representante'
          : 'Contrato social e dados dos representantes',
        subtitle: isIndividualCompany
          ? 'Agora precisamos dos dados pessoais do representante legal:'
          : 'Agora precisamos dos dados pessoais dos representantes legais:',
        position: 'right',
      },
    });

    window.localStorage.removeItem(ONBOARDING_TOKEN);
  }, [setLayout, isIndividualCompany]);

  const removeFormFromList = (indexArgument: number) => {
    setForms((forms) => forms.filter((_, index) => index !== indexArgument));
    setIsToastVisibleForRemoveInMemory(true);
  };

  const handleClickAddForm = () => {
    setForms((forms) => {
      return [...forms, { status: 'pendente', type: 'secondary' }];
    });
  };

  function checkDataDuplication(
    values: Values,
    indexArgument: number,
    field: string
  ) {
    return forms.some((representative, index) => {
      if (index === indexArgument) return false;
      return (
        representative?.values?.[field] === values[field] &&
        values[field] !== ''
      );
    });
  }

  const saveDataRepresentative = async (
    values: Values,
    indexArgument: number,
    status: FormInterface['status']
  ): Promise<boolean> => {
    if (
      checkDataDuplication(values, indexArgument, 'cpf') ||
      checkDataDuplication(values, indexArgument, 'email')
    ) {
      setIsModalAlertDataDoubleOpen(true);
      return false;
    }

    setForms((forms) => {
      return forms.map((form, index) =>
        index === indexArgument ? { ...forms[index], values, status } : form
      );
    });

    return true;
  };

  const handleSubmit = async (
    values: Values,
    indexArgument: number
  ): Promise<boolean> => {
    setFormRepresentativeSubmited(true);
    return await saveDataRepresentative(
      values,
      indexArgument,
      values.allFields ? 'concluído' : 'concluído-parcial'
    );
  };

  const handleChange = (event: any) => {
    setFormRepresentativeSubmited(false);
  };

  const handleClickBack = () => {
    navigate('/');
  };

  const handleClickClose = () => {
    setOpen(false);
  };

  const handleClickConfirmSms = async () => {
    const payload = await formatPayload(forms);

    await saveRepresentatives(onboardId, payload);
    amplitude.track({
      event_type: 'legal_representative_data_inserted',
      event_properties: {
        contract_inserted: payload.social_contract,
      },
    });

    navigate('/representantes/envio-de-documentos-e-selfie', {
      state: location.state,
    });
  };

  const handleClickCloseModalAlertDataDouble = () => {
    setIsModalAlertDataDoubleOpen(false);
  };

  const modalButtonWithTextOk = {
    text: 'Ok',
    onClick: handleClickCloseModalAlertDataDouble,
  };

  const modalPrimaryButton = {
    text: 'Continuar',
    loading,
    onClick: handleClickConfirmSms,
  };

  const modalSecondaryButton = {
    text: 'Cancelar',
    onClick: handleClickClose,
  };

  const handleClickNext = () => {
    const formStatusNotPending = ['concluído', 'concluído-parcial'];
    const formPending =
      forms.some(({ status }) => !formStatusNotPending.includes(status)) ||
      !formRepresentativeSubmited;
    setIsToastVisible(formPending);
    setOpen(!formPending);
  };

  const handleValidationChange = (index: number, isValid: boolean) => {
    setFormValidityList((prev) => {
      const newList = [...prev];
      newList[index] = isValid;
      return newList;
    });
  };

  const isAnyFormInvalid = formValidityList.some(
    (isValid) => isValid === false
  );

  return (
    <Styled.Container>
      {!isIndividualCompany && (
        <Styled.ScreenWarning message="Atenção! Nesta etapa é essencial o cadastro de todos os responsáveis legais da empresa indicados no contrato social." />
      )}
      {forms.map((form, index) => {
        return (
          <Form
            key={index}
            {...form}
            setIsToastVisibleForm={setIsToastVisibleForm}
            setIsToastVisibleForRepresentativeSavedInMemory={
              setIsToastVisibleForRepresentativeSavedInMemory
            }
            isIndividualCompany={isIndividualCompany}
            removeForm={() => removeFormFromList(index)}
            onSubmit={(values) => handleSubmit(values, index)}
            onChange={handleChange}
            onValidationChange={(isValid: boolean) =>
              handleValidationChange(index, isValid)
            }
          />
        );
      })}
      {!isIndividualCompany && (
        <Styled.ButtonWithPlus
          disabled={isPrimaryFormPendent}
          onClick={handleClickAddForm}
        >
          Adicionar outro representante legal
        </Styled.ButtonWithPlus>
      )}
      <Toast
        variant="error"
        title="Atenção aos campos obrigatórios."
        isVisible={isToastVisibleForm}
        lifetime={5000}
        onDismiss={() => setIsToastVisibleForm(false)}
      />
      <Toast
        variant="error"
        title="Representante com dados pendentes. Salve os dados do representante antes de continuar"
        isVisible={isToastVisible}
        lifetime={5000}
        onDismiss={() => setIsToastVisible(false)}
      />
      <Toast
        variant="success"
        title="Representante salvo com sucesso"
        isVisible={isToastVisibleForRepresentativeSavedInMemory}
        lifetime={5000}
        onDismiss={() => setIsToastVisibleForRepresentativeSavedInMemory(false)}
      />
      <Toast
        variant="success"
        title="Representante excluido com sucesso."
        isVisible={isToastVisibleForRemoveInMemory}
        lifetime={5000}
        onDismiss={() => setIsToastVisibleForRemoveInMemory(false)}
      />
      <Footer
        marginTop="auto"
        marginTopSM="64px"
        havePaddingBottom
        rightButton={
          <ButtonWithArrowRight
            variant="primary"
            width="100%"
            onClick={handleClickNext}
            disabled={isAnyFormInvalid}
          >
            Continuar
          </ButtonWithArrowRight>
        }
        leftButton={
          <Button variant="tertiary" width="100%" onClick={handleClickBack}>
            Voltar
          </Button>
        }
      />
      <Modal
        buttons={
          loading
            ? [, modalPrimaryButton]
            : [modalSecondaryButton, modalPrimaryButton]
        }
        highlightedMessage={!loading && 'Deseja continuar?'}
        onClose={!loading && handleClickClose}
        open={open}
        message="Ao clicar em continuar, será enviado um SMS para que cada representante dê continuidade ao cadastro e envio de documentos pessoais via celular."
        title="Continuar e enviar SMS"
      />
      <Modal
        buttons={[, modalButtonWithTextOk]}
        message="Verifique se o cpf ou e-mail estão duplicados entre os representantes."
        onClose={handleClickCloseModalAlertDataDouble}
        open={isModalAlertDataDoubleOpen}
        title="Dados duplicados"
      />
    </Styled.Container>
  );
}

export default Register;
